import { useMemo, useState } from 'react';

import { t } from '../../../lib/i18n';
import { type AuctionScheduleItem } from '../types';
import { UKRAINE_TIMEZONE, cn, toDateKey } from '../utils';
import { CalendarDayCard } from './calendar-day-card';
import { MonthGrid } from './month-grid';

interface Props {
  auctions: AuctionScheduleItem[];
  loading?: boolean;
  className?: string;
}

const SELECTED_FMT = new Intl.DateTimeFormat('uk-UA', {
  timeZone: UKRAINE_TIMEZONE,
  day: 'numeric',
  month: 'long',
});

/**
 * Calendar view — the month grid beside the selected day's closings. Not on the
 * artboard (which draws the table only); kept because it is how an operator
 * finds a date, and restyled onto Design D tokens so the screen reads as one.
 */
export function AuctionScheduleCalendarEditor({ auctions, loading, className }: Props) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [viewMonth, setViewMonth] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const auctionsByDate = useMemo(() => {
    const grouped: Record<string, AuctionScheduleItem[]> = {};
    auctions.forEach((auction) => {
      if (!auction.end_date) return;
      const endDate = new Date(auction.end_date);
      if (isNaN(endDate.getTime())) return;
      const key = toDateKey(endDate);
      (grouped[key] ??= []).push(auction);
    });
    return grouped;
  }, [auctions]);

  const selectedDateAuctions = useMemo(() => {
    if (!selectedDate || isNaN(selectedDate.getTime())) return [];
    return auctionsByDate[toDateKey(selectedDate)] || [];
  }, [selectedDate, auctionsByDate]);

  if (loading) {
    return (
      <section
        className={cn(
          'rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-6 text-center text-[13px] text-cf-ink-subtle lg:px-[18px]',
          className,
        )}
      >
        {t('auctionSchedule.board.loading')}
      </section>
    );
  }

  const shiftMonth = (delta: number) =>
    setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));

  return (
    <div className={cn('flex flex-col gap-3 lg:grid lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)] lg:items-start lg:gap-5', className)}>
      <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-4 lg:px-[18px]">
        <MonthGrid
          viewMonth={viewMonth}
          selectedDate={selectedDate}
          auctionsByDate={auctionsByDate}
          onSelectDate={setSelectedDate}
          onPrevMonth={() => shiftMonth(-1)}
          onNextMonth={() => shiftMonth(1)}
        />
      </section>

      <section className="overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface">
        <div className="border-b border-cf-line-soft px-3.5 py-3 lg:px-[18px] lg:py-3.5">
          <h2 className="text-base font-semibold tracking-[-0.02em] lg:text-[17px]">
            {isNaN(selectedDate.getTime())
              ? t('auctionSchedule.board.none')
              : SELECTED_FMT.format(selectedDate)}
          </h2>
        </div>

        {selectedDateAuctions.length === 0 ? (
          <p className="px-3.5 py-8 text-center text-[13px] text-cf-ink-subtle lg:px-[18px]">
            {t('auctionSchedule.calendar.noAuctions')}
          </p>
        ) : (
          <div className="flex flex-col gap-2 p-2.5 lg:p-3">
            {selectedDateAuctions.map((auction) => (
              <CalendarDayCard key={auction._id} auction={auction} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
